import React, { useEffect, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';

// ─── Engine badge config ──────────────────────────────────────────────────────
const ENGINE = {
  lead:  { label: 'LE',   color: '#3b82f6', bg: 'rgba(59,130,246,0.12)',  title: 'Lead Engine' },
  t2m:   { label: 'T2M',  color: '#eab308', bg: 'rgba(234,179,8,0.12)',   title: 'Trial to Member' },
  pb101: { label: 'PB101',color: '#a855f7', bg: 'rgba(168,85,247,0.12)',  title: 'PB101' },
  // Sprint 21 continuation — pipeline-detected Run Results, not a staff flag.
  identity_unmatched: {
    label: 'UPLOAD', color: '#f97316', bg: 'rgba(249,115,22,0.12)',
    title: 'Upload Pipeline — Unmatched Record',
  },
};

function EngineBadge({ module }) {
  const e = ENGINE[module] || ENGINE.lead;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      background: e.bg, border: `1px solid ${e.color}40`,
      borderRadius: 4, padding: '2px 7px',
      fontSize: 9, fontWeight: 800, color: e.color, letterSpacing: '0.04em',
    }} title={e.title}>
      {e.label}
    </span>
  );
}

function StatusPill({ status }) {
  const config = {
    open:       { color: '#ef4444', label: 'Open' },
    resolved:   { color: '#22c55e', label: 'Resolved' },
    returned:   { color: '#3b82f6', label: 'Returned to Queue' },
    suppressed: { color: '#6b7280', label: 'Suppressed' },
  };
  const c = config[status] || config.open;
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '2px 8px',
      borderRadius: 4, color: c.color,
      background: `${c.color}15`,
      border: `1px solid ${c.color}30`,
    }}>
      {c.label}
    </span>
  );
}

// ─── Single mismatch card ─────────────────────────────────────────────────────
function MismatchCard({ item, isAdmin, onResolve, onReturn, onSuppress, onAddNote }) {
  const [expanded, setExpanded]   = useState(false);
  const [adminNote, setAdminNote] = useState(item.admin_note || '');
  const [saving, setSaving]       = useState(false);

  const flaggedAt = item.flagged_at
    ? new Date(item.flagged_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '—';

  return (
    <div style={{
      background: 'var(--surface-2)',
      border: `1px solid ${item.status === 'open' ? 'rgba(239,68,68,0.25)' : 'var(--border)'}`,
      borderRadius: 8, marginBottom: 10, overflow: 'hidden',
    }}>
      {/* Card header */}
      <div
        style={{ padding: '12px 14px', cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: 10 }}
        onClick={() => setExpanded(v => !v)}
      >
        <EngineBadge module={item.module} />

        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>
              {item.module === 'identity_unmatched'
                ? 'Unmatched upload record'
                : (item.member_name || item.member_id)}
            </span>
            <StatusPill status={item.status} />
            <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
              {item.location_id}
            </span>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
            Flagged by {item.flagged_by_name} · {flaggedAt}
          </div>
          <div style={{
            fontSize: 12, color: 'var(--text)', marginTop: 5,
            padding: '6px 10px', background: 'var(--surface-3)',
            borderRadius: 5, borderLeft: '2px solid #ef4444',
            lineHeight: 1.5,
          }}>
            {item.flag_note}
          </div>
        </div>

        <span style={{ color: 'var(--text-secondary)', fontSize: 12, flexShrink: 0 }}>
          {expanded ? '▴' : '▾'}
        </span>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div style={{ padding: '0 14px 14px', borderTop: '1px solid var(--border)' }}>

          {/* Record details */}
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr',
            gap: '6px 16px', marginTop: 12, marginBottom: 14,
          }}>
            {item.member_email && (
              <div>
                <div style={{ fontSize: 10, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Email</div>
                <div style={{ fontSize: 12, color: 'var(--text)' }}>{item.member_email}</div>
              </div>
            )}
            {item.original_pipeline && (
              <div>
                <div style={{ fontSize: 10, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Pipeline</div>
                <div style={{ fontSize: 12, color: 'var(--text)', textTransform: 'capitalize' }}>{item.original_pipeline.replace(/_/g, ' ')}</div>
              </div>
            )}
            {item.original_status && (
              <div>
                <div style={{ fontSize: 10, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Status at Flag</div>
                <div style={{ fontSize: 12, color: 'var(--text)', textTransform: 'capitalize' }}>{item.original_status}</div>
              </div>
            )}
            <div>
              <div style={{ fontSize: 10, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Record ID</div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{item.record_id}</div>
            </div>
          </div>

          {/* Admin note */}
          {isAdmin && (
            <div style={{ marginBottom: 12 }}>
              <label style={{
                display: 'block', fontSize: 10, fontWeight: 700,
                color: 'var(--text-secondary)', textTransform: 'uppercase',
                letterSpacing: '0.06em', marginBottom: 5,
              }}>
                Admin Note
              </label>
              <textarea
                className="field-textarea"
                value={adminNote}
                onChange={e => setAdminNote(e.target.value)}
                placeholder="Add your diagnosis or resolution note…"
                style={{ minHeight: 70, fontSize: 12 }}
              />
            </div>
          )}

          {/* Show existing admin note if not admin */}
          {!isAdmin && item.admin_note && (
            <div style={{
              padding: '8px 10px', borderRadius: 5,
              background: 'rgba(59,130,246,0.08)',
              border: '1px solid rgba(59,130,246,0.2)',
              fontSize: 12, color: 'var(--text)',
              marginBottom: 12, lineHeight: 1.5,
            }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#3b82f6', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Admin Response
              </div>
              {item.admin_note}
            </div>
          )}

          {/* Actions */}
          {item.status === 'open' && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {isAdmin && (
                <>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => onReturn(item, adminNote)}
                    disabled={saving}
                  >
                    ↩ Return to Queue
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => onSuppress(item, adminNote)}
                    disabled={saving}
                  >
                    🚫 Suppress
                  </button>
                  {adminNote !== (item.admin_note || '') && (
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={async () => {
                        setSaving(true);
                        await onAddNote(item, adminNote);
                        setSaving(false);
                      }}
                      disabled={saving}
                    >
                      💾 Save Note
                    </button>
                  )}
                </>
              )}
            </div>
          )}

          {item.status !== 'open' && item.admin_reviewed_by && (
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>
              Resolved by {item.admin_reviewed_by} · {item.admin_reviewed_at ? new Date(item.admin_reviewed_at).toLocaleDateString() : ''}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main module ──────────────────────────────────────────────────────────────
export default function MismatchModule() {
  const { get, post } = useApi();
  const { user, role } = useAuth();
  const isAdmin = role === 'admin';

  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState('open'); // open | resolved | all
  // Sprint 21 continuation — Run Results (pipeline-detected, flagged_by_user_id
  // === 'system') vs Staff Flags (a real person used the Flag button). Kept
  // as its own filter dimension rather than folded into `filter` above, since
  // status (open/resolved) and source (staff/system) are independent axes —
  // you want to see open items of either source, or all of one source
  // regardless of status.
  const [source, setSource]   = useState('all'); // all | staff | system

  function load() {
    setLoading(true);
    get('/mismatch')
      .then(d => setItems(d?.items || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, []);

  const filtered = items.filter(i => {
    if (filter === 'open' && i.status !== 'open') return false;
    if (filter === 'resolved' && !['resolved','returned','suppressed'].includes(i.status)) return false;
    if (source === 'staff' && i.flagged_by_user_id === 'system') return false;
    if (source === 'system' && i.flagged_by_user_id !== 'system') return false;
    return true;
  });

  const staffCount  = items.filter(i => i.flagged_by_user_id !== 'system').length;
  const systemCount = items.filter(i => i.flagged_by_user_id === 'system').length;

  const openCount = items.filter(i => i.status === 'open').length;

  async function handleReturn(item, adminNote) {
    await post(`/mismatch/${item.mismatch_id}/resolve`, { action: 'return', adminNote });
    load();
  }

  async function handleSuppress(item, adminNote) {
    await post(`/mismatch/${item.mismatch_id}/resolve`, { action: 'suppress', adminNote });
    load();
  }

  async function handleAddNote(item, adminNote) {
    await post(`/mismatch/${item.mismatch_id}/note`, { adminNote });
    load();
  }

  return (
    <div className="module-page">
      <div className="module-header">
        <span style={{ fontSize: 22 }}>🚩</span>
        <h1 className="module-title">
          Mismatch Queue
          {openCount > 0 && (
            <span style={{
              marginLeft: 10, fontSize: 13, fontWeight: 700,
              background: 'rgba(239,68,68,0.15)', color: '#ef4444',
              border: '1px solid rgba(239,68,68,0.3)',
              borderRadius: 12, padding: '2px 10px',
            }}>
              {openCount} open
            </span>
          )}
        </h1>
      </div>

      {/* Info banner */}
      <div style={{
        background: 'rgba(234,179,8,0.07)',
        border: '1px solid rgba(234,179,8,0.2)',
        borderRadius: 8, padding: '10px 14px',
        fontSize: 12, color: 'var(--text-secondary)',
        marginBottom: '1.25rem', lineHeight: 1.6,
      }}>
        {isAdmin
          ? 'Records flagged by staff as data mismatches, plus Run Results the upload pipeline couldn\'t confidently match on its own. Pipeline will not touch staff-flagged records until resolved. Review the note, diagnose with Bryon, then Return to Queue or Suppress.'
          : 'Records you have flagged as data mismatches. Bryon will review and respond. The pipeline will not touch these records until they are resolved.'}
      </div>

      {/* Source tabs — Sprint 21 continuation: pipeline Run Results vs staff-flagged records */}
      <div style={{ display: 'flex', gap: 8, marginBottom: '0.6rem' }}>
        {[
          { key: 'all',    label: `All Sources (${items.length})` },
          { key: 'staff',  label: `Staff Flags (${staffCount})` },
          { key: 'system', label: `Run Results (${systemCount})` },
        ].map(s => (
          <button
            key={s.key}
            onClick={() => setSource(s.key)}
            className={`module-tab ${source === s.key ? 'active' : ''}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: '1rem' }}>
        {[
          { key: 'open',     label: `Open (${items.filter(i => i.status === 'open').length})` },
          { key: 'resolved', label: `Resolved (${items.filter(i => i.status !== 'open').length})` },
          { key: 'all',      label: `All (${items.length})` },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`module-tab ${filter === f.key ? 'active' : ''}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* List */}
      {loading && (
        <div style={{ color: 'var(--text-secondary)', fontSize: 13, padding: '20px 0' }}>
          Loading…
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div style={{
          textAlign: 'center', padding: '40px 0',
          color: 'var(--text-secondary)', fontSize: 13,
        }}>
          {filter === 'open' ? '✅ No open mismatches.' : 'No records in this filter.'}
        </div>
      )}

      {!loading && filtered.map(item => (
        <MismatchCard
          key={item.mismatch_id}
          item={item}
          isAdmin={isAdmin}
          onResolve={() => {}}
          onReturn={handleReturn}
          onSuppress={handleSuppress}
          onAddNote={handleAddNote}
        />
      ))}
    </div>
  );
}
